import React from 'react'
import { motion } from 'framer-motion'
import { X, Building } from 'lucide-react'
import deepWindLogo from '@/assets/deepwind-logo.png'
import { useLanguage } from '@/contexts/LanguageContext'
import Button from '@/components/Button'
import miniTurbineIcon from '@/assets/wind_turbine_icon.png'
import ErrorBanner from '@/components/ErrorBanner'
import { useTurbineDetailViewModel } from './useTurbineDetailViewModel'
import CommentSidebar from './components/CommentSidebar'
import LoadingScreen from './components/LoadingScreen'
import DetailHeader from './components/DetailHeader'
import RoomPhotosSection from './components/RoomPhotosSection'
import CategoryPhotosSection from './components/CategoryPhotosSection'
import AllPhotosSection from './components/AllPhotosSection'
import VideoSection from './components/VideoSection'
import ReportSection from './components/ReportSection'
import DeleteConfirmModal from './components/DeleteConfirmModal'
import PhotoModal from './components/PhotoModal'
import PhotoComparisonModal from './components/PhotoComparisonModal'
import VideoPlayerModal from './components/VideoPlayerModal'

const TurbineDetailScreen: React.FC = () => {
  const { t } = useLanguage()

  const {
    turbine,
    loading,
    error,
    currentUser,
    isSuperAdmin,
    isCustomer,
    canDeletePhotos,
    allPhotos,
    categoryPhotos,
    photosLoading,
    availableYears,
    filteredPhotosBySearch,
    photosLoadError,
    retryLoadPhotos,
    reportsLoadError,
    retryLoadReports,
    videosLoadError,
    retryLoadVideos,
    selectedFiles,
    setSelectedFiles,
    selectedBladeFiles,
    uploading,
    uploadError,
    setUploadError,
    dragOver,
    handleFileSelect,
    handleBladeFileSelect,
    removeSelectedBladeFile,
    handleUploadSelectedBlade,
    handleUploadSelected,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleUploadClick,
    modalPhoto,
    setModalPhoto,
    openPhotoModal,
    closeModal,
    deleteModal,
    closeDeleteModal,
    deleteError,
    setDeleteError,
    isDeletingPhoto,
    handleDeletePhotoFromList,
    handleDeletePhoto,
    confirmDeletePhoto,
    isBulkDeleteMode,
    toggleBulkDeleteMode,
    selectedPhotosForBulkDelete,
    togglePhotoForBulkDelete,
    handleBulkDelete,
    isDeletingBulk,
    bulkDeleteError,
    setBulkDeleteError,
    isComparisonMode,
    toggleComparisonMode,
    selectedPhotosForComparison,
    togglePhotoSelection,
    openComparisonModal,
    showComparisonModal,
    setShowComparisonModal,
    searchTerm,
    setSearchTerm,
    selectedYear,
    setSelectedYear,
    allPhotosSelectedYear,
    setAllPhotosSelectedYear,
    allPhotosSelectedWeek,
    setAllPhotosSelectedWeek,
    selectedPhotoType,
    setSelectedPhotoType,
    weekOptions,
    yearOptions,
    dronePhotoYear,
    setDronePhotoYear,
    operatorPhotoYear,
    setOperatorPhotoYear,
    robotPhotoYear,
    setRobotPhotoYear,
    selectedDroneBlade,
    setSelectedDroneBlade,
    selectedOperatorBlade,
    setSelectedOperatorBlade,
    selectedRobotBlade,
    setSelectedRobotBlade,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    isYearDropdownOpen,
    setIsYearDropdownOpen,
    isAllPhotosYearDropdownOpen,
    setIsAllPhotosYearDropdownOpen,
    isAllPhotosWeekDropdownOpen,
    setIsAllPhotosWeekDropdownOpen,
    isTypeDropdownOpen,
    setIsTypeDropdownOpen,
    isDroneYearDropdownOpen,
    setIsDroneYearDropdownOpen,
    isOperatorYearDropdownOpen,
    setIsOperatorYearDropdownOpen,
    isRobotYearDropdownOpen,
    setIsRobotYearDropdownOpen,
    isDroneBladeDropdownOpen,
    setIsDroneBladeDropdownOpen,
    isOperatorBladeDropdownOpen,
    setIsOperatorBladeDropdownOpen,
    isRobotBladeDropdownOpen,
    setIsRobotBladeDropdownOpen,
    yearDropdownRef,
    allPhotosYearDropdownRef,
    allPhotosWeekDropdownRef,
    typeDropdownRef,
    bladeRoomSelection,
    setBladeRoomSelection,
    isBladeRoomDropdownOpen,
    setIsBladeRoomDropdownOpen,
    selectedReports,
    uploadingReports,
    reportUploadError,
    existingReports,
    loadingReports,
    reportYear,
    setReportYear,
    reportWeek,
    setReportWeek,
    isReportYearDropdownOpen,
    setIsReportYearDropdownOpen,
    isReportWeekDropdownOpen,
    setIsReportWeekDropdownOpen,
    reportWeekDropdownRef,
    handleReportSelect,
    removeSelectedReport,
    uploadReports,
    deleteReport,
    cleanFileName,
    selectedVideoType,
    setSelectedVideoType,
    selectedVideos,
    uploadingVideos,
    videoUploadError,
    videoYear,
    setVideoYear,
    videoWeek,
    setVideoWeek,
    existingVideos,
    loadingVideos,
    isVideoTypeDropdownOpen,
    setIsVideoTypeDropdownOpen,
    isVideoYearDropdownOpen,
    setIsVideoYearDropdownOpen,
    isVideoWeekDropdownOpen,
    setIsVideoWeekDropdownOpen,
    playingVideoId,
    setPlayingVideoId,
    videoTypeDropdownRef,
    videoYearDropdownRef,
    videoWeekDropdownRef,
    handleVideoSelect,
    removeSelectedVideo,
    uploadVideos,
    deleteVideoHandler,
    isCommentsOpen,
    setIsCommentsOpen,
  } = useTurbineDetailViewModel()

  // Don't render until we have user info to prevent cache issues
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading user data...</p>
        </div>
      </div>
    )
  }
  // Loading state
  if (loading) {
    return <LoadingScreen />
  }

  // Error state - only show error if we're not loading and have an actual error
  if (error && !turbine) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center">
                  <img src={deepWindLogo} alt="VentraWind Logo" className="w-8 h-8 object-contain" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">{t.dashboard.companyName}</h1>
                  <p className="text-xs text-gray-500">{t.dashboard.turbineManagement}</p>
                </div>
              </div>
            </div>
          </div>
        </header>
        
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-xl shadow-sm border p-8 max-w-md mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{t.errors.title}</h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <div className="space-y-3">
                <Button
                  onClick={() => window.location.reload()}
                  className="w-full bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
                >
                  Tekrar Dene
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  // If we get here, turbine must be loaded (not null)
  if (!turbine) {
    return null // This shouldn't happen, but prevents TypeScript errors
  }

  return (
    <div key={`turbine-detail-${currentUser?.role || 'guest'}`} className="min-h-screen bg-gray-50" style={{ pointerEvents: 'auto' }}>
      {/* Header with Topbar */}
      <DetailHeader
        isSuperAdmin={isSuperAdmin}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        isCommentsOpen={isCommentsOpen}
        setIsCommentsOpen={setIsCommentsOpen}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" style={{ pointerEvents: 'auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ pointerEvents: 'auto' }}
        >


          {/* Turbine Header */}
          <div className="bg-white rounded-xl shadow-sm border p-8 mb-8">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 flex items-center justify-center flex-shrink-0">
                  <img 
                    src={miniTurbineIcon}
                    alt="Turbine Icon"
                    className="w-16 h-16 object-contain"
                  />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">{turbine.name || `${turbine.city} Türbini`}</h1>
                  <p className="text-lg text-gray-600">{turbine.location}</p>
                </div>
              </div>
              <div className="text-right space-y-2">
                <div className="space-y-1">
                  <p className="text-lg text-gray-900 font-bold">{turbine.code}</p>
                  <p className="text-sm text-gray-500">
                    <Building className="w-4 h-4 inline mr-1" />
                    {turbine.company_name}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Turbin verisi ekranda ama sonradan bir hata olustu (ornegin yenileme
              basarisiz): tam ekran hata sayfasi gosterilemez, banner ile bildirilir. */}
          {error && turbine && (
            <ErrorBanner message={error} className="mb-8" onRetry={() => window.location.reload()} />
          )}

          {/* Room Photos Section - Hidden for Customer */}
          <RoomPhotosSection
            isCustomer={isCustomer}
            allPhotos={allPhotos}
            selectedBladeFiles={selectedBladeFiles}
            uploading={uploading}
            dragOver={dragOver}
            handleBladeFileSelect={handleBladeFileSelect}
            removeSelectedBladeFile={removeSelectedBladeFile}
            handleUploadSelectedBlade={handleUploadSelectedBlade}
            handleDragOver={handleDragOver}
            handleDragLeave={handleDragLeave}
            handleDrop={handleDrop}
            handleUploadClick={handleUploadClick}
            openPhotoModal={openPhotoModal}
            handleDeletePhotoFromList={handleDeletePhotoFromList}
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            yearOptions={yearOptions}
            isYearDropdownOpen={isYearDropdownOpen}
            setIsYearDropdownOpen={setIsYearDropdownOpen}
            yearDropdownRef={yearDropdownRef}
            bladeRoomSelection={bladeRoomSelection}
            setBladeRoomSelection={setBladeRoomSelection}
            isBladeRoomDropdownOpen={isBladeRoomDropdownOpen}
            setIsBladeRoomDropdownOpen={setIsBladeRoomDropdownOpen}
          />

          {/* Additional Photo Categories + Customer View-Only + Upload Error */}
          <CategoryPhotosSection
            turbine={turbine}
            isCustomer={isCustomer}
            categoryPhotos={categoryPhotos}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
            uploading={uploading}
            uploadError={uploadError}
            setUploadError={setUploadError}
            handleFileSelect={handleFileSelect}
            handleUploadSelected={handleUploadSelected}
            yearOptions={yearOptions}
            dronePhotoYear={dronePhotoYear}
            setDronePhotoYear={setDronePhotoYear}
            operatorPhotoYear={operatorPhotoYear}
            setOperatorPhotoYear={setOperatorPhotoYear}
            robotPhotoYear={robotPhotoYear}
            setRobotPhotoYear={setRobotPhotoYear}
            selectedDroneBlade={selectedDroneBlade}
            setSelectedDroneBlade={setSelectedDroneBlade}
            selectedOperatorBlade={selectedOperatorBlade}
            setSelectedOperatorBlade={setSelectedOperatorBlade}
            selectedRobotBlade={selectedRobotBlade}
            setSelectedRobotBlade={setSelectedRobotBlade}
            isDroneYearDropdownOpen={isDroneYearDropdownOpen}
            setIsDroneYearDropdownOpen={setIsDroneYearDropdownOpen}
            isOperatorYearDropdownOpen={isOperatorYearDropdownOpen}
            setIsOperatorYearDropdownOpen={setIsOperatorYearDropdownOpen}
            isRobotYearDropdownOpen={isRobotYearDropdownOpen}
            setIsRobotYearDropdownOpen={setIsRobotYearDropdownOpen}
            isDroneBladeDropdownOpen={isDroneBladeDropdownOpen}
            setIsDroneBladeDropdownOpen={setIsDroneBladeDropdownOpen}
            isOperatorBladeDropdownOpen={isOperatorBladeDropdownOpen}
            setIsOperatorBladeDropdownOpen={setIsOperatorBladeDropdownOpen}
            isRobotBladeDropdownOpen={isRobotBladeDropdownOpen}
            setIsRobotBladeDropdownOpen={setIsRobotBladeDropdownOpen}
          />

          {/* All Photos Section */}
          <AllPhotosSection
            canDeletePhotos={canDeletePhotos}
            photosLoading={photosLoading}
            availableYears={availableYears}
            filteredPhotosBySearch={filteredPhotosBySearch}
            setModalPhoto={setModalPhoto}
            handleDeletePhotoFromList={handleDeletePhotoFromList}
            isBulkDeleteMode={isBulkDeleteMode}
            toggleBulkDeleteMode={toggleBulkDeleteMode}
            selectedPhotosForBulkDelete={selectedPhotosForBulkDelete}
            togglePhotoForBulkDelete={togglePhotoForBulkDelete}
            handleBulkDelete={handleBulkDelete}
            isDeletingBulk={isDeletingBulk}
            isComparisonMode={isComparisonMode}
            toggleComparisonMode={toggleComparisonMode}
            selectedPhotosForComparison={selectedPhotosForComparison}
            togglePhotoSelection={togglePhotoSelection}
            openComparisonModal={openComparisonModal}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            allPhotosSelectedYear={allPhotosSelectedYear}
            setAllPhotosSelectedYear={setAllPhotosSelectedYear}
            allPhotosSelectedWeek={allPhotosSelectedWeek}
            setAllPhotosSelectedWeek={setAllPhotosSelectedWeek}
            selectedPhotoType={selectedPhotoType}
            setSelectedPhotoType={setSelectedPhotoType}
            weekOptions={weekOptions}
            isAllPhotosYearDropdownOpen={isAllPhotosYearDropdownOpen}
            setIsAllPhotosYearDropdownOpen={setIsAllPhotosYearDropdownOpen}
            isAllPhotosWeekDropdownOpen={isAllPhotosWeekDropdownOpen}
            setIsAllPhotosWeekDropdownOpen={setIsAllPhotosWeekDropdownOpen}
            isTypeDropdownOpen={isTypeDropdownOpen}
            setIsTypeDropdownOpen={setIsTypeDropdownOpen}
            allPhotosYearDropdownRef={allPhotosYearDropdownRef}
            allPhotosWeekDropdownRef={allPhotosWeekDropdownRef}
            typeDropdownRef={typeDropdownRef}
            photosLoadError={photosLoadError}
            retryLoadPhotos={retryLoadPhotos}
            bulkDeleteError={bulkDeleteError}
            setBulkDeleteError={setBulkDeleteError}
          />

          {/* Video Upload Section - Hidden for Customer */}
          <VideoSection
            isSuperAdmin={isSuperAdmin}
            isCustomer={isCustomer}
            weekOptions={weekOptions}
            yearOptions={yearOptions}
            selectedVideoType={selectedVideoType}
            setSelectedVideoType={setSelectedVideoType}
            selectedVideos={selectedVideos}
            uploadingVideos={uploadingVideos}
            videoUploadError={videoUploadError}
            videoYear={videoYear}
            setVideoYear={setVideoYear}
            videoWeek={videoWeek}
            setVideoWeek={setVideoWeek}
            existingVideos={existingVideos}
            loadingVideos={loadingVideos}
            isVideoTypeDropdownOpen={isVideoTypeDropdownOpen}
            setIsVideoTypeDropdownOpen={setIsVideoTypeDropdownOpen}
            isVideoYearDropdownOpen={isVideoYearDropdownOpen}
            setIsVideoYearDropdownOpen={setIsVideoYearDropdownOpen}
            isVideoWeekDropdownOpen={isVideoWeekDropdownOpen}
            setIsVideoWeekDropdownOpen={setIsVideoWeekDropdownOpen}
            setPlayingVideoId={setPlayingVideoId}
            videoTypeDropdownRef={videoTypeDropdownRef}
            videoYearDropdownRef={videoYearDropdownRef}
            videoWeekDropdownRef={videoWeekDropdownRef}
            handleVideoSelect={handleVideoSelect}
            removeSelectedVideo={removeSelectedVideo}
            uploadVideos={uploadVideos}
            deleteVideoHandler={deleteVideoHandler}
            videosLoadError={videosLoadError}
            retryLoadVideos={retryLoadVideos}
          />

          {/* Türbin Rapor Yükleme Alanı - Hidden for Customer */}
          <ReportSection
            isCustomer={isCustomer}
            selectedYear={selectedYear}
            weekOptions={weekOptions}
            yearOptions={yearOptions}
            selectedReports={selectedReports}
            uploadingReports={uploadingReports}
            reportUploadError={reportUploadError}
            existingReports={existingReports}
            loadingReports={loadingReports}
            reportYear={reportYear}
            setReportYear={setReportYear}
            reportWeek={reportWeek}
            setReportWeek={setReportWeek}
            isReportYearDropdownOpen={isReportYearDropdownOpen}
            setIsReportYearDropdownOpen={setIsReportYearDropdownOpen}
            isReportWeekDropdownOpen={isReportWeekDropdownOpen}
            setIsReportWeekDropdownOpen={setIsReportWeekDropdownOpen}
            reportWeekDropdownRef={reportWeekDropdownRef}
            handleReportSelect={handleReportSelect}
            removeSelectedReport={removeSelectedReport}
            uploadReports={uploadReports}
            deleteReport={deleteReport}
            cleanFileName={cleanFileName}
            reportsLoadError={reportsLoadError}
            retryLoadReports={retryLoadReports}
          />

        </motion.div>
      </main>

      {/* Bildirimler artik uygulama capindaki NotificationProvider'dan geliyor.
          Buradaki lokal NotificationModal kaldirildi: kalsaydi ayni bildirim
          hem toast hem modal olarak CIFT gorunurdu. */}

      {/* Photo Modal */}
      <PhotoModal
        canDeletePhotos={canDeletePhotos}
        modalPhoto={modalPhoto}
        setModalPhoto={setModalPhoto}
        closeModal={closeModal}
        handleDeletePhoto={handleDeletePhoto}
      />

      {/* Photo Comparison Modal */}
      <PhotoComparisonModal
        selectedPhotosForComparison={selectedPhotosForComparison}
        showComparisonModal={showComparisonModal}
        setShowComparisonModal={setShowComparisonModal}
      />

      {/* Video Player Modal */}
      <VideoPlayerModal
        existingVideos={existingVideos}
        playingVideoId={playingVideoId}
        setPlayingVideoId={setPlayingVideoId}
      />

      {/* Delete Confirmation Modal.
          PhotoModal'dan SONRA render ediliyor: PhotoModal hala kendi overlay'ini
          kullaniyor ve ortak Modal ile AYNI z-index katmaninda (Z_INDEX.modal).
          Esit z-index'te DOM sirasi belirleyicidir; onay dialogu fotograf
          lightbox'inin arkasinda kalmamali. */}
      <DeleteConfirmModal
        deleteModal={deleteModal}
        closeDeleteModal={closeDeleteModal}
        confirmDeletePhoto={confirmDeletePhoto}
        deleteError={deleteError}
        setDeleteError={setDeleteError}
        isDeletingPhoto={isDeletingPhoto}
      />

      {/* Comments Sidebar */}
      {turbine && (
        <CommentSidebar
          turbineId={turbine.id}
          isOpen={isCommentsOpen}
          onClose={() => setIsCommentsOpen(false)}
        />
      )}

    </div>
  )
}

export default TurbineDetailScreen
